import React, { useState, useEffect } from 'react';
import Layout from '../../components/common/Layout';
import { bookService } from '../../services/bookService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  ChartBarIcon,
  BookOpenIcon,
  ArrowTrendingUpIcon,
  StarIcon
} from '@heroicons/react/24/outline';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [booksByGenre, setBooksByGenre] = useState<any>({});
  const [booksByStatus, setBooksByStatus] = useState<any>({});
  const [monthlyAdditions, setMonthlyAdditions] = useState<any>({});
  const [topRatedBooks, setTopRatedBooks] = useState<any>({});
  const [totalBooks, setTotalBooks] = useState(0);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch books data
      const books = await bookService.getBooks();
      setTotalBooks(books.length);

      // Books by Genre
      const genreData = await bookService.getBooksByGenre();
             setBooksByGenre({
         labels: genreData.map((item: any) => item.genre),
         datasets: [{
           label: 'Number of Books',
           data: genreData.map((item: any) => item.count),
          backgroundColor: [
            '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
            '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
          ],
          borderColor: [
            '#2563EB', '#DC2626', '#059669', '#D97706',
            '#7C3AED', '#DB2777', '#0D9488', '#EA580C'
          ],
          borderWidth: 1
        }]
      });

      // Books by Status
      const statusData = await bookService.getBooksByStatus();
      setBooksByStatus({
        labels: statusData.map(item => item.status.charAt(0).toUpperCase() + item.status.slice(1)),
        datasets: [{
          label: 'Number of Books',
          data: statusData.map(item => item.count),
          backgroundColor: ['#10B981', '#F59E0B', '#EF4444', '#6B7280'],
          borderColor: ['#059669', '#D97706', '#DC2626', '#4B5563'],
          borderWidth: 2
        }]
      });

      // Monthly Additions (mock data for now)
      const last6Months = [];
      const currentDate = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        last6Months.push(date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
      }

      setMonthlyAdditions({
        labels: last6Months,
        datasets: [{
          label: 'Books Added',
          data: [12, 19, 8, 15, 22, 18], // Mock data
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        }]
      });

      // Top Rated Books
      const topRated = await bookService.getTopRatedBooks(5);
      setTopRatedBooks({
        labels: topRated.map(book => book.title.length > 20 ? book.title.substring(0, 20) + '...' : book.title),
        datasets: [{
          label: 'Rating',
          data: topRated.map(book => book.rating),
          backgroundColor: '#F59E0B',
          borderColor: '#D97706',
          borderWidth: 1
        }]
      });

    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Insights and statistics about your book collection
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BookOpenIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Books
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {totalBooks}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Genres
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {booksByGenre.labels?.length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                                 <div className="flex-shrink-0">
                   <ArrowTrendingUpIcon className="h-6 w-6 text-gray-400" />
                 </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      This Month
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {monthlyAdditions.datasets?.[0]?.data?.slice(-1)[0] || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <StarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Avg Rating
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {topRatedBooks.datasets?.[0]?.data?.length > 0 
                        ? (topRatedBooks.datasets[0].data.reduce((a: number, b: number) => a + b, 0) / topRatedBooks.datasets[0].data.length).toFixed(1)
                        : '0.0'
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Books by Genre */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Books by Genre
            </h3>
            <div className="h-64">
              {booksByGenre.labels && (
                <Bar data={booksByGenre} options={chartOptions} />
              )}
            </div>
          </div>

          {/* Monthly Additions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Monthly Book Additions
            </h3>
            <div className="h-64">
              {monthlyAdditions.labels && (
                <Line data={monthlyAdditions} options={chartOptions} />
              )}
            </div>
          </div>

          {/* Book Status Distribution */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Book Status Distribution
            </h3>
            <div className="h-64">
              {booksByStatus.labels && (
                <Doughnut data={booksByStatus} options={doughnutOptions} />
              )}
            </div>
          </div>

          {/* Top Rated Books */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Top Rated Books
            </h3>
            <div className="h-64">
              {topRatedBooks.labels && topRatedBooks.labels.length > 0 ? (
                <Bar 
                  data={topRatedBooks} 
                  options={{
                    ...chartOptions,
                    indexAxis: 'y' as const,
                    scales: {
                      x: {
                        beginAtZero: true,
                        max: 5
                      }
                    }
                  }} 
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <StarIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2">No rated books yet</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Insights */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Collection Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {booksByStatus.datasets?.[0]?.data?.[0] || 0}
              </div>
              <div className="text-sm text-gray-500">Available Books</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {booksByStatus.datasets?.[0]?.data?.[1] || 0}
              </div>
              <div className="text-sm text-gray-500">Currently Borrowed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {((booksByStatus.datasets?.[0]?.data?.[0] || 0) / totalBooks * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500">Availability Rate</div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Analytics; 